import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  AlertTriangle,
  Cake,
  Award,
  Coffee,
  RefreshCw,
  Building,
  ChevronRight,
  PartyPopper,
  Calendar,
  TrendingUp,
  ChevronLeft,
  DollarSign,
  BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function HRDashboardWidget() {
  const [data, setData] = useState(null);
  const [shiftOverview, setShiftOverview] = useState(null);
  const [wageReport, setWageReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState('');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [reportPeriod, setReportPeriod] = useState('week');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedStore ? `?store=${encodeURIComponent(selectedStore)}` : '';
      const [statsResponse, rosterResponse, wageResponse] = await Promise.all([
        apiRequest(`/hr-dashboard/stats${params}`),
        apiRequest(`/hr-dashboard/roster-summary${params}`),
        apiRequest(`/hr-dashboard/wage-report?period=${reportPeriod}`)
      ]);
      setData(statsResponse);
      setShiftOverview(rosterResponse);
      setWageReport(wageResponse);
    } catch (error) {
      console.error('Failed to fetch HR dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStore, reportPeriod]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <Card className="h-full" data-testid="hr-dashboard-loading">
        <CardContent className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-red-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { stats, celebrations, stores, can_see_all_stores } = data;

  return (
    <Card className="h-full overflow-hidden" data-testid="hr-dashboard-widget">
      <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white pb-3 px-3">
        <div className="flex items-center justify-between flex-wrap gap-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Users className="h-4 w-4 flex-shrink-0" />
            <CardTitle className="text-base font-bold truncate">HR Dashboard</CardTitle>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {can_see_all_stores && stores.length > 0 && (
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 text-white border-0 outline-none max-w-[70px]"
                data-testid="store-filter"
              >
                <option value="">All</option>
                {stores.map(store => (
                  <option key={store} value={store}>{store}</option>
                ))}
              </select>
            )}
            <button onClick={fetchData} className="p-0.5 hover:bg-white/20 rounded flex-shrink-0">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <p className="text-red-100 text-[10px] mt-0.5 truncate">
          {data.date} • {data.time}
        </p>
      </CardHeader>

      <CardContent className="p-2 space-y-2 overflow-hidden">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-1">
          {/* Total Staff */}
          <div className="bg-slate-50 rounded-lg p-1.5 text-center min-w-0">
            <div className="w-6 h-6 mx-auto bg-slate-200 rounded-full flex items-center justify-center mb-0.5">
              <Users className="h-3 w-3 text-slate-600" />
            </div>
            <p className="text-lg font-bold text-slate-800">{stats.total_staff}</p>
            <p className="text-[9px] text-slate-500 truncate">Staff</p>
          </div>
          
          {/* On Shift */}
          <div className="bg-green-50 rounded-lg p-1.5 text-center min-w-0">
            <div className="w-6 h-6 mx-auto bg-green-200 rounded-full flex items-center justify-center mb-0.5">
              <UserCheck className="h-3 w-3 text-green-600" />
            </div>
            <p className="text-lg font-bold text-green-700">{stats.on_shift}</p>
            <p className="text-[9px] text-green-600 truncate">Shift</p>
          </div>
          
          {/* On Break */}
          <div className="bg-yellow-50 rounded-lg p-1.5 text-center min-w-0">
            <div className="w-6 h-6 mx-auto bg-yellow-200 rounded-full flex items-center justify-center mb-0.5">
              <Coffee className="h-3 w-3 text-yellow-600" />
            </div>
            <p className="text-lg font-bold text-yellow-700">{stats.on_break}</p>
            <p className="text-[9px] text-yellow-600 truncate">Break</p>
          </div>
        </div>

        {/* Alerts Row */}
        <div className="grid grid-cols-2 gap-1">
          {/* Late */}
          <div 
            className={`rounded-lg p-1.5 flex items-center gap-1.5 cursor-pointer transition-colors min-w-0 ${
              stats.late > 0 ? 'bg-orange-50 hover:bg-orange-100' : 'bg-slate-50'
            }`}
            onClick={() => stats.late > 0 && navigate('/admin/time-clock')}
          >
            <div className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center ${
              stats.late > 0 ? 'bg-orange-200' : 'bg-slate-200'
            }`}>
              <Clock className={`h-3 w-3 ${stats.late > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
            </div>
            <div className="min-w-0">
              <p className={`text-base font-bold ${stats.late > 0 ? 'text-orange-700' : 'text-slate-600'}`}>
                {stats.late}
              </p>
              <p className={`text-[9px] truncate ${stats.late > 0 ? 'text-orange-600' : 'text-slate-500'}`}>Late</p>
            </div>
          </div>
          
          {/* Absent */}
          <div 
            className={`rounded-lg p-1.5 flex items-center gap-1.5 cursor-pointer transition-colors min-w-0 ${
              stats.absent > 0 ? 'bg-red-50 hover:bg-red-100' : 'bg-slate-50'
            }`}
            onClick={() => stats.absent > 0 && navigate('/admin/time-clock')}
          >
            <div className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center ${
              stats.absent > 0 ? 'bg-red-200' : 'bg-slate-200'
            }`}>
              <UserX className={`h-3 w-3 ${stats.absent > 0 ? 'text-red-600' : 'text-slate-400'}`} />
            </div>
            <div className="min-w-0">
              <p className={`text-base font-bold ${stats.absent > 0 ? 'text-red-700' : 'text-slate-600'}`}>
                {stats.absent}
              </p>
              <p className={`text-[9px] truncate ${stats.absent > 0 ? 'text-red-600' : 'text-slate-500'}`}>Absent</p>
            </div>
          </div>
        </div>

        {/* Late Staff Alert */}
        {data.staff_late && data.staff_late.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Running Late</span>
            </div>
            <div className="space-y-1">
              {data.staff_late.slice(0, 3).map(staff => (
                <div key={staff.id} className="flex items-center justify-between text-xs">
                  <span className="text-orange-700">{staff.name}</span>
                  <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                    {staff.minutes_late}m late
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Shift Overview */}
        {shiftOverview && shiftOverview.today_coverage && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              Today's Shift Coverage
            </h3>
            
            {/* Coverage Timeline */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              {/* Time slots */}
              <div className="flex justify-between text-[10px] text-slate-400 px-1">
                <span>6AM</span>
                <span>9AM</span>
                <span>12PM</span>
                <span>3PM</span>
                <span>6PM</span>
                <span>9PM</span>
              </div>
              
              {/* Coverage bar */}
              <div className="relative h-8 bg-slate-200 rounded-full overflow-hidden">
                {shiftOverview.today_coverage.time_slots?.map((slot, index) => {
                  const startPercent = ((slot.hour - 6) / 18) * 100;
                  const widthPercent = (1 / 18) * 100;
                  const coverageLevel = slot.staff_count >= slot.recommended ? 'bg-green-500' : 
                                       slot.staff_count >= slot.recommended * 0.5 ? 'bg-yellow-500' : 'bg-red-500';
                  return (
                    <div
                      key={index}
                      className={`absolute top-0 bottom-0 ${coverageLevel} transition-all`}
                      style={{ left: `${Math.max(0, startPercent)}%`, width: `${widthPercent}%` }}
                      title={`${slot.hour}:00 - ${slot.staff_count}/${slot.recommended} staff`}
                    />
                  );
                })}
                
                {/* Current time indicator */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-slate-800 z-10"
                  style={{ 
                    left: `${Math.min(100, Math.max(0, ((new Date().getHours() - 6) / 18) * 100))}%` 
                  }}
                />
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-4 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Fully staffed
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Needs attention
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Understaffed
                </span>
              </div>
            </div>
            
            {/* Department breakdown */}
            {shiftOverview.today_coverage.departments && (
              <div className="mt-3 space-y-2">
                {shiftOverview.today_coverage.departments.slice(0, 4).map(dept => (
                  <div key={dept.name} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{dept.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        dept.current >= dept.required ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {dept.current}/{dept.required}
                      </span>
                      {dept.current < dept.required && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 text-orange-600 border-orange-300">
                          -{dept.required - dept.current}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Weekly trend */}
            {shiftOverview.week_summary && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    This Week
                  </span>
                  <span className="font-medium text-slate-700">
                    {shiftOverview.week_summary.total_shifts} shifts scheduled
                  </span>
                </div>
                <div className="flex gap-1 mt-2">
                  {shiftOverview.week_summary.daily?.map((day, index) => (
                    <div key={index} className="flex-1 text-center">
                      <div 
                        className={`h-6 rounded-sm ${
                          day.shift_count > 5 ? 'bg-green-400' : 
                          day.shift_count > 2 ? 'bg-green-300' : 
                          day.shift_count > 0 ? 'bg-green-200' : 'bg-slate-200'
                        }`}
                        title={`${day.date}: ${day.shift_count} shifts`}
                      />
                      <span className="text-[9px] text-slate-400">{day.day}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compact Mini Calendar */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              Calendar
            </h3>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <ChevronLeft className="h-4 w-4 text-slate-500" />
              </button>
              <span className="text-xs font-medium text-slate-600 min-w-[80px] text-center">
                {calendarDate.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
              </span>
              <button 
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <ChevronRight className="h-4 w-4 text-slate-500" />
              </button>
            </div>
          </div>
          
          {/* Mini Calendar Grid */}
          <div className="bg-slate-50 rounded-lg p-2">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="text-center text-[10px] font-medium text-slate-400">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const year = calendarDate.getFullYear();
                const month = calendarDate.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const today = new Date();
                const days = [];
                
                // Empty cells for days before month starts
                for (let i = 0; i < firstDay; i++) {
                  days.push(<div key={`empty-${i}`} className="h-6" />);
                }
                
                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const isToday = today.getDate() === day && 
                                  today.getMonth() === month && 
                                  today.getFullYear() === year;
                  const isWeekend = new Date(year, month, day).getDay() === 0 || 
                                    new Date(year, month, day).getDay() === 6;
                  
                  days.push(
                    <button
                      key={day}
                      onClick={() => navigate(`/admin/roster?date=${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)}
                      className={`h-6 w-full rounded text-[11px] transition-colors ${
                        isToday 
                          ? 'bg-red-600 text-white font-bold' 
                          : isWeekend
                            ? 'text-slate-400 hover:bg-slate-200'
                            : 'text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {day}
                    </button>
                  );
                }
                
                return days;
              })()}
            </div>
          </div>
          
          {/* Quick links */}
          <div className="flex gap-2 mt-2">
            <button 
              onClick={() => {
                setCalendarDate(new Date());
                navigate('/admin/roster');
              }}
              className="flex-1 text-xs py-1.5 px-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
            >
              Today's Roster
            </button>
            <button 
              onClick={() => navigate('/admin/leave')}
              className="flex-1 text-xs py-1.5 px-2 bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors"
            >
              Leave Requests
            </button>
          </div>
        </div>

        {/* Celebrations Section */}
        {(celebrations.birthdays_today.length > 0 || 
          celebrations.anniversaries_today.length > 0 ||
          celebrations.birthdays_this_week.length > 0 ||
          celebrations.anniversaries_this_week.length > 0) && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <PartyPopper className="h-4 w-4 text-pink-500" />
              Celebrations
            </h3>
            
            {/* Birthdays Today */}
            {celebrations.birthdays_today.length > 0 && (
              <div className="bg-pink-50 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Cake className="h-4 w-4 text-pink-600" />
                  <span className="text-sm font-medium text-pink-800">Birthdays Today!</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {celebrations.birthdays_today.map(person => (
                    <Badge key={person.id} className="bg-pink-100 text-pink-700 border-0">
                      {person.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Anniversaries Today */}
            {celebrations.anniversaries_today.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Work Anniversaries Today!</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {celebrations.anniversaries_today.map(person => (
                    <Badge key={person.id} className="bg-purple-100 text-purple-700 border-0">
                      {person.name} ({person.years}y)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upcoming This Week */}
            {(celebrations.birthdays_this_week.length > 0 || celebrations.anniversaries_this_week.length > 0) && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-600 mb-2">Upcoming This Week</p>
                <div className="space-y-1 text-xs">
                  {celebrations.birthdays_this_week.slice(0, 3).map(person => (
                    <div key={person.id} className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Cake className="h-3 w-3 text-pink-500" />
                        {person.name}
                      </span>
                      <span className="text-slate-500">{person.date}</span>
                    </div>
                  ))}
                  {celebrations.anniversaries_this_week.slice(0, 3).map(person => (
                    <div key={person.id} className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-purple-500" />
                        {person.name} ({person.years}y)
                      </span>
                      <span className="text-slate-500">{person.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Wage Report Section - HR Manager Only */}
        {wageReport && (
          <div className="border-t pt-4 mt-4" data-testid="wage-report-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="font-semibold text-slate-800">Wage Report</span>
              </div>
              <select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="text-xs border rounded px-2 py-1 bg-white"
                data-testid="wage-period-select"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-blue-700">{wageReport.summary?.total_hours || 0}</div>
                <div className="text-xs text-blue-600">Total Hours</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-green-700">${wageReport.summary?.total_wages?.toLocaleString() || 0}</div>
                <div className="text-xs text-green-600">Total Wages</div>
              </div>
            </div>
            
            {/* Store Breakdown */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              <div className="flex items-center gap-1 mb-2">
                <BarChart3 className="h-3 w-3 text-slate-500" />
                <span className="text-xs font-medium text-slate-600">By Store</span>
              </div>
              {wageReport.by_store?.map((store) => (
                <div key={store.store} className="bg-slate-50 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 truncate">{store.store}</span>
                    <Badge variant="outline" className="text-xs">
                      {store.staff_count} staff
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {store.total_hours} hrs
                    </span>
                    <span className="text-green-600 font-medium">
                      ${store.total_wages.toLocaleString()}
                    </span>
                  </div>
                  {/* Position Breakdown */}
                  {store.breakdown?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                      {store.breakdown.slice(0, 3).map((pos) => (
                        <div key={pos.position} className="flex items-center justify-between text-xs text-slate-500">
                          <span className="truncate">{pos.position}</span>
                          <span>${pos.wages.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Period Info */}
            <div className="mt-2 text-center text-xs text-slate-400">
              {wageReport.period?.start} to {wageReport.period?.end}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="border-t pt-4">
          <button
            onClick={() => navigate('/admin/roster')}
            className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="view-roster-btn"
          >
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">View Roster</span>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
